// React components
import { useState, useEffect, useMemo, useContext } from "react";

// Material UI & Material Dashboard components
import { Grid } from "@mui/material";
import MDAvatar from "components/MDAvatar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDDropzone from "components/MDDropzone";
import MDTypography from "components/MDTypography";

// Services
import partCodesService from "services/part-code-service";
import changeLogService from "services/change-log-service";
import { AuthContext } from "context";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ImageModal from "e4acomponents/ImageModal";


export default function EditImage({
    part,
    setPart,
    image,
    setImage
}) {
    // Dummy image will remove before release
    const dummyImg = "https://ami-spotlight.footbridge.tilkal.com/resources/images/image.jpg";

    // Holds the image shown in current image section
    const [imageUrl, setImageUrl] = useState(dummyImg);
    // Stores the new image until upload
    const [newImage, setNewImage] = useState("");

    const [openImageModal, setOpenImageModal] = useState(false);

    const authContext = useContext(AuthContext);

    // Fetches the image from the server using the s3Key of the partcode
    useEffect(() => {
        (async () => {
            if (part.has_image) {
                console.log("fetching ", part.imagePath);
                const response = await partCodesService.getImage(part.imagePath);
                setImageUrl(response);
            }
        })();
    }, [part.has_image, part.imagePath]);

    // Whenever an image is added to the dropzone, it is stored in newImage
    const onImageAdd = (file) => {
        console.log(file);
        setNewImage(file);
    };

    // Deletes previous partcode's image then replace it with the new image
    const changeImage = async () => {
        if (!newImage) {
            return;
        }
        await partCodesService.deleteFile(part.imagePath);

        let payload = {
            data: {
                attributes: {
                    filename: newImage.upload.filename,
                    dataUrl: newImage.dataURL,
                    folderpath: part.generatedUID
                }
            }
        };

        const response = await partCodesService.uploadFile(payload);
        setPart({ ...part, imagePath: response.s3Key });
        setImage(response.s3Key);

        // Capturing the image change in the coresponding changelog
        const changeLog = {
            data: {
                attributes: {
                    partCode: part.id,
                    change: {
                        type: "Image Change",
                        description: "Part code image has been changed",
                        date: new Date(),
                        user: await authContext.getCurrentUser(),
                    }
                }
            }
        }
        await changeLogService.addChange(changeLog);

        // Update the partcode's image path in the database
        payload = {
            data: {
                type: "partCode",
                id: part.id,
                attributes: {
                    imagePath: response.s3Key
                }
            }
        }

        await partCodesService.updatePartKeys(payload, part.id);
    };

    return (
        <MDBox>
            <ImageModal
                open={openImageModal}
                onClose={() => setOpenImageModal(false)}
                mediaURL={imageUrl}
                alt="Part Code Image"
            />

            <MDTypography variant="h5" fontWeight="bold" mt={1} mb={2}>
                Edit Image
            </MDTypography>

            <Grid container spacing={0} direction="row">
                <Grid item xs={12} sm={3}>
                    {/* Displays the partcode's current image */}
                    <MDTypography variant="h6" fontWeight="light" mt={1} mb={2}>
                        Current Image :
                    </MDTypography>

                    <MDBox
                        onClick={() => setOpenImageModal(true)}
                        sx={{ cursor: "pointer" }}
                    >
                        <img
                            src={imageUrl}
                            alt="Part Code"
                            style={{
                                width: '140px',
                                height: '150px',
                                borderRadius: '6px',
                                objectFit: 'cover',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                        />
                    </MDBox>

                </Grid>
                <Grid item xs={12} sm={9}>
                    {/* Dropzone to upload new image */}
                    <MDTypography variant="h6" fontWeight="light" mt={1} mb={2}>
                        Upload New Image :
                    </MDTypography>

                    <Grid container spacing={1}>
                        <Grid item xs={12} md={10}>
                            {useMemo(
                                () => (
                                    <MDDropzone
                                        options={{ addRemoveLinks: true, maxFiles: 1, acceptedFiles: 'image/*' }}
                                        onFileAdd={onImageAdd}
                                        acceptedFiles="image/*"
                                    />
                                ),
                                []
                            )}
                        </Grid>

                        <Grid item xs={12} md={2}>
                            <MDBox mb={1}>
                                <MDButton
                                    fullWidth
                                    variant="contained"
                                    color="primary"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    size="large"
                                    onClick={changeImage}
                                    sx={{ height: '150px' }}
                                >
                                    <CloudUploadIcon color="white" sx={{ transform: 'scale(2)' }} />
                                </MDButton>
                            </MDBox>
                        </Grid>
                    </Grid>

                </Grid>

            </Grid>
        </MDBox>
    )
}